/**
 * Entitlements utility — single source of truth for plan-gating.
 *
 * Usage:
 *   const { elite, standard } = getEntitlements(userProfile)
 *   if (!standard) return <UpgradePrompt />
 *
 * Profile shape accepted (flexible — works with AuthContext userProfile or raw DB row):
 *   - preferences.billing.plan  (webhook-written, most reliable)
 *   - active_plan               (legacy field)
 *   - subscription_status       (legacy 'premium' flag)
 *   - elite_academy_access      (admin toggle)
 *   - admin_override            (super-admin bypass)
 */

export interface Entitlements {
  /** Plan key, e.g. 'homeschool_single' | 'edu_elite' | 'edu_bundle' | '' */
  plan: string;
  /** True if user can access Elite Academy courses */
  elite: boolean;
  /** True if user can access Standard (84-subject) courses */
  standard: boolean;
  /**
   * True if user can access the Bible-Journey / faith catalog ($7 church seat).
   * Granted by church plan keys OR by any elite/standard entitlement.
   */
  bibleSchool: boolean;
  /** True if user has any active paid access (elite OR standard) */
  isPaid: boolean;
  /** True if user has an active Still Waters University subscription */
  university: boolean;
}

export function getEntitlements(profile: Record<string, any> | null | undefined): Entitlements {
  if (!profile) {
    return { plan: '', elite: false, standard: false, bibleSchool: false, isPaid: false, university: false };
  }

  // 1. Webhook-written plan key — most reliable (written by stripe-webhook on payment success)
  const billingPlan: string =
    profile?.preferences?.billing?.plan ??
    profile?.active_plan ??
    '';

  // 2. Expiry check (respects subscription_end_date or plan_expires_at if present)
  const expiryDate = profile?.subscription_end_date || profile?.plan_expires_at;
  const isExpired = !!expiryDate && new Date(expiryDate).getTime() < Date.now();

  // 3. Legacy 'premium' subscription_status (kept for backwards compat)
  const legacyPremium = profile?.subscription_status === 'premium' && !isExpired;

  // 4. Admin overrides
  const adminOverride: boolean =
    !!profile?.admin_override ||
    !!profile?.elite_academy_access;

  const hasPlanKey = billingPlan !== '' && !isExpired;

  // 5. Derive flags
  const isElitePlan = ['edu_elite', 'edu_bundle', 'sponsor_elite_seat', 'sponsor_bundle_seat', 'corporate_elite_pack', 'corporate_bundle_pack'].includes(billingPlan);
  const isStandardPlan = ['homeschool_single', 'sponsor_seat_monthly', 'corporate_edu_pack', 'pay_it_forward'].includes(billingPlan);
  const isChurchPlan = ['sage-church-seat', 'church_congregation', 'church_growing'].includes(billingPlan);
  // University: any plan key that starts with 'university_', plus admin overrides and legacy premium
  const isUniversityPlan = billingPlan.startsWith('university_') || !!profile?.university_access;

  const elite =
    adminOverride ||
    (hasPlanKey && isElitePlan) ||
    (legacyPremium && !billingPlan); // legacy users only (grandfathered)

  const standard =
    adminOverride ||
    elite ||
    (hasPlanKey && isStandardPlan) ||
    (legacyPremium && !billingPlan);

  // bibleSchool: unlocked by church plan, or by any paid academy access (superset)
  const bibleSchool =
    adminOverride ||
    elite ||
    standard ||
    (hasPlanKey && isChurchPlan);

  const university =
    adminOverride ||
    (hasPlanKey && isUniversityPlan);

  const isPaid = (standard || elite || university) && (!isExpired || adminOverride);

  return { plan: hasPlanKey ? billingPlan : '', elite, standard, bibleSchool, isPaid, university };
}

/**
 * Typed accessor for preferences.self_learn_active.
 * `preferences` is typed as Json in generated DB types — direct property
 * access fails tsc. All call sites use this helper instead.
 */
export function isSelfLearnActive(profile: Record<string, any> | null | undefined): boolean {
  return !!((profile?.preferences as Record<string, any> | null | undefined)?.self_learn_active);
}
